import { Fragment, useEffect } from 'react';
import {
  Route,
  Routes,
  unstable_HistoryRouter as Router,
  Navigate,
} from 'react-router-dom';

import history from '../../history';
import Paths from '../../constants/Paths';
import AuthService from './../../services/AuthService';
import OrderProductsProcessService from '../../services/OrderProductsProcessService';

import AllowNotificationsDialog from '../allow-notifications-dialog';
import { AccountProfile } from '../account-profile';
import CustomSnackbar from '../common/custom-snackbar/CustomSnackbar';
import ExternalAuthPage from '../external-auth-page';
import { HelpCenterPage } from '../help-center-page';
import { LoginPage } from '../login-page';
import MarketingPage from '../marketing-page';
import { MenuPage } from 'features/menu-page';
import OrdersKanbanPage from '../orders-kanban-page';
import OrdersHistorical from '../orders-historical';
import { SalesSummary } from '../sales-summary';
import SettingsProfile from '../settings-profile';
import { StoresSchedulesPage } from '../stores-schedules-page';
import { SuspendedStoreAlertSnackbar } from '../suspended-store-alert-snackbar';
import { StoreManagerPage } from '../store-manager-page';
import { OnBoardingDialog } from '../onboarding-dialog';
import { autoAcceptService } from 'features/settings-page/auto-accept';
import { default as MainLayoutLegacy } from '../main-layout';

const checkAuthentication = Page => {
  if (!AuthService.isAuthenticated()) {
    return AuthService.UNIFIED_LOGIN_ENABLED ? (
      <UnifiedLoginRedirect />
    ) : (
      <Navigate to={Paths.LOGIN} />
    );
  }
  return (
    <AllowNotificationsDialog>
      <Page auth={AuthService} />
    </AllowNotificationsDialog>
  );
};

const checkLogin = () => {
  if (AuthService.isAuthenticated()) {
    return <Navigate to={Paths.ORDERS_KANBAN} />;
  }
  return AuthService.mustUseLegacyLogin(history.location.search) ? (
    <LoginPage auth={AuthService} />
  ) : (
    <UnifiedLoginRedirect />
  );
};

const handleExternalAuth = () => {
  return AuthService.isAuthenticated() ? (
    <Navigate to={Paths.ORDERS_KANBAN} />
  ) : (
    <ExternalAuthPage />
  );
};

const Routing = ({
  snackBar,
  startAppUp,
  closeSnackBar,
  intl: { formatMessage },
}) => {
  OrderProductsProcessService.setFormatMessage(formatMessage);

  const handleSnackBarClose = () => {
    closeSnackBar();
  };
  const _isAuthenticated = AuthService.isAuthenticated();

  useEffect(() => {
    startAppUp();
  }, []);

  useEffect(() => {
    _isAuthenticated && autoAcceptService.sync();
  }, [_isAuthenticated]);

  return (
    <>
      <Router history={history}>
        <ContainersOverRoutes>
          <Route path={Paths.LOGIN} element={checkLogin()} />
          <Route path={Paths.AUTH} element={handleExternalAuth()} />
          <Route
            path={Paths.ORDERS_KANBAN}
            element={checkAuthentication(OrdersKanbanPage)}
          />
          <Route
            path={Paths.ORDERS_HISTORY}
            element={checkAuthentication(OrdersHistorical)}
          />
          <Route
            path={Paths.SALES_SUMMARY}
            element={checkAuthentication(SalesSummary)}
          />
          <Route path={Paths.MENU} element={checkAuthentication(MenuPage)} />
          <Route
            path={Paths.MY_ACCOUNT}
            element={checkAuthentication(AccountProfile)}
          />
          <Route
            path={`${Paths.STORES_SCHEDULES}/:id`}
            element={checkAuthentication(StoresSchedulesPage)}
          />
          <Route
            path={Paths.STORES_SCHEDULES}
            element={checkAuthentication(StoresSchedulesPage)}
          />
          <Route
            path={Paths.MARKETING}
            element={checkAuthentication(MarketingPage)}
          />
          <Route
            path={Paths.SETTINGS}
            element={checkAuthentication(SettingsProfile)}
          />
          <Route
            path={Paths.STORE_MANAGEMENT}
            element={checkAuthentication(StoreManagerPage)}
          />
          <Route
            path={Paths.HELP_CENTER_WITH_CATEGORY_AND_ARTICLE}
            element={checkAuthentication(HelpCenterPage)}
          />
          <Route
            path={Paths.HELP_CENTER_WITH_CATEGORY}
            element={checkAuthentication(HelpCenterPage)}
          />
          <Route
            path={Paths.HELP_CENTER}
            element={checkAuthentication(HelpCenterPage)}
          />
          <Route path='*' element={<Navigate to={Paths.ORDERS_KANBAN} />} />
        </ContainersOverRoutes>
      </Router>
      <SuspendedStoreAlertSnackbar />
      <CustomSnackbar
        open={snackBar.open}
        message={snackBar.message}
        variant={snackBar.variant}
        onClose={handleSnackBarClose}
        title={snackBar.title}
        autoHideDuration={snackBar.autoHideDuration}
      />
      <OnBoardingDialog />
    </>
  );
};

export default Routing;

/**
 * Full-page redirect to the unified login (RA-6824).
 *
 * Rendered in place of the legacy `<LoginPage>` for any unauthenticated user,
 * so the in-app Aliados login never mounts. The redirect runs in an effect
 * (after commit) and the component paints nothing while the browser leaves.
 * @returns {null}
 */
function UnifiedLoginRedirect() {
  useEffect(() => {
    AuthService.goToUnifiedLogin();
  }, []);
  return null;
}

// Creado para evitar indentación de los componentes al agregar el MainLayout
function ContainersOverRoutes({ children }) {
  const isAuthenticated = AuthService.isAuthenticated();
  const MasterLayout = isAuthenticated ? MainLayoutLegacy : Fragment;
  return (
    <MasterLayout>
      <Routes>{children}</Routes>
    </MasterLayout>
  );
}
